import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Card, CardBody, Button, Spinner } from 'reactstrap';
import { PlusCircle, CheckCircle } from 'lucide-react';
import { useSiteStore } from '../../../stores/Assets/site';

const BranchSelection = ({ updateState, onNext }) => {
    const { t } = useTranslation();
    const { getSites, sites, isLoading } = useSiteStore();

    useEffect(() => {
        getSites();
    }, [getSites]);

    const handleSelectExisting = (site) => {
        // Map API site data to Experience store format
        const siteInfo = {
            businessName: site.name,
            nameAr: site.name_ar,
            address: site.address,
            addressAr: site.address_ar,
            phone: site.phone,
            latitude: site.latitude,
            longitude: site.longitude,
            logo: site.image_url // URL
        };

        updateState({
            selectedSiteId: site.site_id,
            siteInfo: siteInfo
        });
        onNext();
    };

    const handleCreateNew = () => {
        updateState({
            selectedSiteId: null,
            siteInfo: null // Clear previous info if any
        });
        onNext();
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner color="primary" />
            </div>
        );
    }

    // If no sites, you might want to auto-redirect or just show "Create New" only.
    // For now, we show the options.

    return (
        <div className="branch-selection-step">
            <div className="text-center mb-5">
                <h2 className="fw-bold text-dark">{t('Select a Branch')}</h2>
                <p className="text-muted">{t('Choose an existing branch to update or create a new one.')}</p>
            </div>

            <Row className="g-4 justify-content-center">
                {/* Create New Option */}
                <Col md={6} lg={4}>
                    <Card
                        className="h-100 cursor-pointer border-2 border-dashed border-secondary border-opacity-25 hover-shadow transition-all"
                        onClick={handleCreateNew}
                        style={{ minHeight: '200px', backgroundColor: 'transparent' }}
                    >
                        <CardBody className="d-flex flex-column align-items-center justify-content-center text-center p-4">
                            <div className="mb-3 text-primary bg-primary bg-opacity-10 p-3 rounded-circle">
                                <PlusCircle size={32} />
                            </div>
                            <h5 className="fw-bold mb-2">{t('Create New Branch')}</h5>
                            <p className="text-muted small mb-0">{t('Start fresh with a new location')}</p>
                        </CardBody>
                    </Card>
                </Col>

                {/* Existing Sites */}
                {sites?.map((site) => (
                    <Col key={site.site_id} md={6} lg={4}>
                        <Card
                            className="h-100 cursor-pointer border hover-shadow transition-all"
                            onClick={() => handleSelectExisting(site)}
                            style={{ minHeight: '200px' }}
                        >
                            <CardBody className="p-4 d-flex flex-column">
                                <div className="d-flex align-items-center mb-3">
                                    {site.image_url ? (
                                        <img
                                            src={site.image_url}
                                            alt={site.name}
                                            className="rounded-circle me-3 object-fit-cover"
                                            width="48"
                                            height="48"
                                        />
                                    ) : (
                                        <div className="rounded-circle me-3 bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48 }}>
                                            <span className="fw-bold text-secondary">{site.name?.charAt(0)}</span>
                                        </div>
                                    )}
                                    <div className="text-start overflow-hidden">
                                        <h6 className="fw-bold mb-0 text-truncate">{site.name}</h6>
                                        <small className="text-muted text-truncate d-block">{site.address || t("No address")}</small>
                                    </div>
                                    <div className="ms-auto">
                                        {/* Visual indicator could go here */}
                                    </div>
                                </div>
                                <div className="mt-auto text-end">
                                    <span className="text-primary small fw-semibold">{t('Select')} <CheckCircle size={14} className="ms-1 align-text-top" /></span>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

export default BranchSelection;
